import { useState, useEffect } from 'react';
import CardNB from '../ui-nb/CardNB';
import ButtonNB from '../ui-nb/ButtonNB';
import { supabase } from '../../lib/supabase';
import { getErrorMessage } from '../../lib/errors';

export interface SparePartData {
  description: string;
  trackingNumber: string;
}

interface CreateSparePartModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId?: string;
  onCreated?: () => void;
  onSave?: (data: SparePartData) => void;
  defaultDescription?: string;
}

type ModalStatus = 'idle' | 'loading' | 'error';

export default function CreateSparePartModal({
  isOpen,
  onClose,
  orderId,
  onCreated,
  onSave,
  defaultDescription = '',
}: CreateSparePartModalProps) {
  const [description, setDescription] = useState(defaultDescription);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [showTracking, setShowTracking] = useState(false);
  const [status, setStatus] = useState<ModalStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [validationError, setValidationError] = useState('');

  // Reset state when modal opens (sync defaultDescription)
  useEffect(() => {
    if (isOpen) {
      setDescription(defaultDescription);
      setTrackingNumber('');
      setShowTracking(false);
      setStatus('idle');
      setErrorMessage('');
      setValidationError('');
    }
  }, [isOpen, defaultDescription]);

  if (!isOpen) return null;

  const handleClose = () => {
    if (status === 'loading') return;
    setDescription('');
    setTrackingNumber('');
    setShowTracking(false);
    setStatus('idle');
    setErrorMessage('');
    setValidationError('');
    onClose();
  };

  const handleSubmit = async () => {
    // Validate
    if (!description.trim()) {
      setValidationError('Description is required');
      return;
    }
    setValidationError('');

    // Deferred mode: pass data to parent, don't insert to DB
    if (onSave) {
      onSave({ description: description.trim(), trackingNumber: trackingNumber.trim() });
      handleClose();
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      const { error: insertError } = await supabase
        .from('spare_parts')
        .insert({
          order_id: orderId!,
          description: description.trim(),
          status: 'sin_pedir',
          tracking_number: trackingNumber.trim() || null,
        });

      if (insertError) throw insertError;

      onCreated?.();
      handleClose();
    } catch (err: unknown) {
      setErrorMessage(getErrorMessage(err) || 'Failed to create spare part');
      setStatus('error');
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
      onClick={handleClose}
    >
      <CardNB
        className="max-w-md w-full mx-4"
        style={{ border: '3px solid #000' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          className="text-xl font-bold text-center mb-6"
          style={{ color: '#000' }}
        >
          New Spare Part
        </h2>

        {/* Description field */}
        <div className="mb-4">
          <label
            htmlFor="sp-description"
            className="block mb-2"
            style={{ fontSize: '0.875rem', fontWeight: 500, color: '#000' }}
          >
            Description *
          </label>
          <textarea
            id="sp-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the spare part..."
            disabled={status === 'loading'}
            rows={3}
            style={{
              width: '100%',
              padding: '0.5rem 0.75rem',
              border: '2px solid #000',
              fontSize: '0.875rem',
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
            aria-label="Description *"
          />
          {validationError && (
            <p className="mt-1" style={{ color: '#D64545', fontSize: '0.75rem' }}>
              {validationError}
            </p>
          )}
        </div>

        {/* Tracking number (collapsed by default) */}
        <div className="mb-4">
          <button
            type="button"
            onClick={() => setShowTracking(!showTracking)}
            className="flex items-center gap-2 mb-2"
            style={{ fontSize: '0.875rem', color: '#525252', cursor: 'pointer', background: 'none', border: 'none', padding: 0, fontFamily: 'inherit' }}
          >
            <span style={{ display: 'inline-block', transition: 'transform 0.2s', transform: showTracking ? 'rotate(90deg)' : 'rotate(0deg)' }}>
              ▶
            </span>
            Tracking number (optional)
          </button>
          {showTracking && (
            <input
              id="sp-tracking"
              type="text"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              placeholder="e.g. TRACK-12345"
              disabled={status === 'loading'}
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '2px solid #000',
                fontSize: '0.875rem',
                fontFamily: 'inherit',
              }}
              aria-label="Tracking number"
            />
          )}
        </div>

        {errorMessage && (
          <p
            className="mb-4"
            style={{ color: '#D64545', fontSize: '0.875rem', textAlign: 'center' }}
          >
            {errorMessage}
          </p>
        )}

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <ButtonNB
            variant="primary"
            disabled={status === 'loading'}
            loading={status === 'loading'}
            onClick={handleSubmit}
            style={{ flex: 1 }}
          >
            Create
          </ButtonNB>
          <ButtonNB
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={status === 'loading'}
          >
            Cancel
          </ButtonNB>
        </div>
      </CardNB>
    </div>
  );
}
